import React, { useEffect, useState } from "react";
import { Bell } from "lucide-react";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = () =>
    api.get("/notifications").then((res) => {
      setItems(res.data.notifications);
      setUnread(res.data.unread);
    }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, []);

  const onOpenChange = async (v) => {
    setOpen(v);
    if (v && unread > 0) {
      try {
        await api.post("/notifications/read");
        setUnread(0);
        setItems((its) => its.map((i) => ({ ...i, read: true })));
      } catch (_) {}
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative h-9 w-9 grid place-items-center rounded-lg border border-white/15 hover:border-white/40 transition-colors"
          aria-label="Notifications"
        >
          <Bell size={18} />
          {unread > 0 && (
            <span
              data-testid="notification-unread-dot"
              className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-primary text-white text-[10px] font-bold grid place-items-center"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 bg-[#111111] border-white/10 p-0" data-testid="notification-panel">
        <div className="px-4 py-3 border-b border-white/10">
          <p className="font-bold text-sm">Notifications</p>
        </div>
        <div className="max-h-80 overflow-y-auto">
          {items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">You're all caught up.</p>
          ) : (
            items.map((n) => (
              <div key={n.id} data-testid="notification-item" className="px-4 py-3 border-b border-white/5 last:border-0">
                <div className="flex items-start gap-2">
                  {!n.read && <span className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0" />}
                  <div className={n.read ? "opacity-70" : ""}>
                    <p className="font-semibold text-sm">{n.title}</p>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-0.5">{n.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
